// M6 — Auth surface. Backed by labourcore-api instead of Supabase Auth.
// Preserves the same export shape as the v49.0 module so callers need no changes.

import { supabase, SUPABASE_READY } from "./supabase-client";
import { lcAudit } from "../audit";

// Compat types — these replace the @supabase/supabase-js imports
export interface Session {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    user_metadata: Record<string, unknown>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface AuthSubscription {
  unsubscribe: () => void;
}

export const auth = {
  async getSession(): Promise<Session | null> {
    if (!SUPABASE_READY) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session || null;
  },
  async signUp(
    email: string,
    password: string,
    metadata?: Record<string, unknown>,
  ) {
    if (!SUPABASE_READY) throw new Error("API not configured");
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    if (error) throw error;
    lcAudit({ event_type: "auth.signed_up", ui_string: email, metadata: { has_metadata: !!metadata } });
    return data;
  },
  async signIn(email: string, password: string) {
    if (!SUPABASE_READY) throw new Error("API not configured");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    lcAudit({ event_type: "auth.signed_in", ui_string: email });
    return data;
  },
  async signOut() {
    if (!SUPABASE_READY) return;
    lcAudit({ event_type: "auth.signed_out" });
    await supabase.auth.signOut();
  },
  onAuthStateChange(handler: (event: string, session: Session | null) => void): AuthSubscription | { unsubscribe: () => void } {
    if (!SUPABASE_READY) return { unsubscribe: () => {} };
    const { data } = supabase.auth.onAuthStateChange(handler as any);
    return data?.subscription || { unsubscribe: () => {} };
  },

  /* MFA stubs — not implemented in labourcore-api */
  mfa: {
    async listFactors() {
      if (!SUPABASE_READY) return { totp: [], phone: [] };
      return { totp: [], phone: [] };
    },
    async getAal() {
      if (!SUPABASE_READY) return { currentLevel: null, nextLevel: null };
      return { currentLevel: null, nextLevel: null };
    },
    async enroll(_friendlyName: string) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async challenge(_factorId: string) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async verify(_args: { factorId: string; challengeId: string; code: string }) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async challengeAndVerify(_args: { factorId: string; code: string }) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
    async unenroll(_factorId: string) {
      console.warn("[auth.mfa] MFA not implemented in labourcore-api");
      throw new Error("MFA not implemented");
    },
  },
};
